import ndisDatabase from '../ndis_pricing_database.json';

/**
 * NDIS Support Category mapping based on item code prefix (first 2 digits)
 * Source: NDIS Pricing Arrangements and Price Limits 2025-26, pp.12-13
 */
const SUPPORT_CATEGORIES = {
  '01': 'Assistance with Daily Life',
  '02': 'Transport',
  '03': 'Consumables',
  '04': 'Social, Economic & Community Participation',
  '05': 'Assistive Technology',
  '06': 'Home Modifications & SDA',
  '07': 'Support Coordination & Recovery Coaching',
  '08': 'Improved Living Arrangements',
  '09': 'Increased Social & Community Participation',
  '10': 'Finding & Keeping a Job',
  '11': 'Improved Relationships',
  '12': 'Improved Health & Wellbeing',
  '13': 'Improved Learning',
  '14': 'Improved Life Choices',
  '15': 'Improved Daily Living Skills',
};

/**
 * Get the proper NDIS support category for an item based on its code prefix
 */
export function getCategoryForItem(item) {
  if (!item?.item_code) return 'Other';
  const prefix = item.item_code.substring(0, 2);
  return SUPPORT_CATEGORIES[prefix] || 'Other';
}

/**
 * Search the NDIS pricing database
 * @param {string} query - Search query
 * @returns {Array} - Matching NDIS items
 */
export function searchNDISItems(query) {
  if (!query || query.trim() === '') {
    return [];
  }

  const queryLower = query.toLowerCase().trim();
  
  // Split query into individual words
  const searchWords = queryLower.split(/\s+/).filter(word => word.length > 0);
  
  return ndisDatabase.filter(item => {
    const searchText = item.search_text ? item.search_text.toLowerCase() : '';
    
    // Check if ALL search words appear in the search text (in any order)
    return searchWords.every(word => searchText.includes(word));
  }).sort((a, b) => {
    // Sort results by relevance
    // Items where the exact phrase appears first
    const aText = a.search_text ? a.search_text.toLowerCase() : '';
    const bText = b.search_text ? b.search_text.toLowerCase() : '';
    
    const aHasExact = aText.includes(queryLower);
    const bHasExact = bText.includes(queryLower);
    
    if (aHasExact && !bHasExact) return -1;
    if (!aHasExact && bHasExact) return 1;
    
    // Then by category match
    if (a.category && searchWords.some(w => a.category.toLowerCase().includes(w))) {
      if (!b.category || !searchWords.some(w => b.category.toLowerCase().includes(w))) {
        return -1;
      }
    }
    
    return 0;
  });
}

/**
 * Get NDIS item by code
 * @param {string} code - NDIS item code
 * @returns {Object|null} - NDIS item or null
 */
export function getNDISItem(code) {
  return ndisDatabase.find(item => item.item_code === code) || null;
}

/**
 * Get all items in a category
 * @param {string} category - Category name
 * @returns {Array} - Items in category
 */
export function getItemsByCategory(category) {
  return ndisDatabase.filter(item => item.category === category);
}

/**
 * Get price for a service based on day and time
 * @param {string} itemCode - Base NDIS item code
 * @param {Date} date - Date of service
 * @returns {Object|null} - Matching item with price
 */
export function getPriceForDateTime(itemCode, date) {
  // Determine day type from date
  const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
  const hour = date.getHours();
  
  let dayType = 'Weekday';
  let timeType = 'Daytime';
  
  if (dayOfWeek === 0) {
    dayType = 'Sunday';
  } else if (dayOfWeek === 6) {
    dayType = 'Saturday';
  } else {
    // Weekday - determine time
    if (hour >= 18 || hour < 6) {
      timeType = 'Evening';
    }
  }
  
  // Find matching item
  // First try to find exact match
  const exactMatch = ndisDatabase.find(item => 
    item.item_code.includes(itemCode.substring(0, 10)) &&
    item.day_type === dayType &&
    (timeType === 'Daytime' || item.time_type === timeType)
  );
  
  if (exactMatch) {
    return exactMatch;
  }
  
  // Fallback to base item code
  return getNDISItem(itemCode);
}

/**
 * Calculate total for shift
 * @param {number} hours - Number of hours
 * @param {number} hourlyRate - Hourly rate
 * @param {number} kilometers - Kilometers traveled
 * @param {number} kmRate - Rate per kilometer (default 1.00)
 * @returns {Object} - Breakdown of costs
 */
export function calculateShiftTotal(hours, hourlyRate, kilometers = 0, kmRate = 1.0) {
  const serviceTotal = hours * hourlyRate;
  const transportTotal = kilometers * kmRate;
  const total = serviceTotal + transportTotal;
  
  return {
    serviceTotal: parseFloat(serviceTotal.toFixed(2)),
    transportTotal: parseFloat(transportTotal.toFixed(2)),
    total: parseFloat(total.toFixed(2))
  };
}

/**
 * Get all unique categories based on item code prefixes
 * @returns {Array} - List of unique categories
 */
export function getAllCategories() {
  const categories = new Set();
  ndisDatabase.forEach(item => {
    categories.add(getCategoryForItem(item));
  });
  return Array.from(categories).sort();
}
